import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Button,
    Form,
    Grid,
    Header,
    Icon,
    Popup,
    Select,
} from "semantic-ui-react";
import {
    DateTimeUtils,
    generateResolver,
    Table,
    ViewMore,
    yup,
} from "dyl-components";
import {
    KEY_CONTACT_ACCOUNT,
    KEY_DATE,
    KEY_PHONE_NUMBER,
    KEY_USER,
    KEY_DURATION,
    KEY_ACTIONS,
    KEY_DIRECTION,
    KEY_SYSTEM_RESULT,
    KEY_NOTE,
    KEY_USER_RESULT,
    KEY_DESTINATION,
    KEY_CALL_TAGS,
} from "shared/constants/CALL_HISTORY_LOGS_COLUMNS";
import { AvatarGroup } from "dyl-components";
import { VoicemailPlayback } from "dyl-components";
import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";
import useModal from "utils/useModal";
import { PhoneUtil } from "utils";

const ReportCallPopup = ({ entry, onClose, onReportCall }) => {
    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid },
    } = useForm({
        defaultValues: {
            problem_type: "",
        },
        mode: "onChange",
        resolver: generateResolver({
            problem_type: yup.string().required("Problem type is required"),
        }),
    });

    const onSubmit = (data) => {
        onReportCall?.(entry, data);
    };

    return (
        <div
            style={{
                padding: ".5rem",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
            }}
        >
            <Grid>
                <Grid.Row>
                    <Grid.Column width={13}>
                        <Header as="h3" color="blue">
                            Report Call Problem
                        </Header>
                    </Grid.Column>
                    <Grid.Column>
                        <Icon
                            name="close"
                            size="large"
                            floated="right"
                            className="fa-solid fa-x"
                            onClick={onClose}
                        />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Field required>
                    <label>Problem Type</label>
                    <Controller
                        name="problem_type"
                        control={control}
                        rules={{ required: "Please select a problem type" }}
                        render={({ field, fieldState }) => (
                            <Select
                                {...field}
                                options={PhoneUtil.CALL_ISSUE_OPTIONS.getOptions()}
                                placeholder="Select Problem Type"
                                onChange={(_, { value }) =>
                                    field.onChange(value)
                                }
                                clearable
                                error={fieldState?.error?.message}
                            />
                        )}
                    />
                </Form.Field>
                <Button
                    disabled={!isValid || !isDirty}
                    floated="right"
                    type="submit"
                    primary
                >
                    Save
                </Button>
            </Form>
        </div>
    );
};

const CallHistoryRow = ({
    entry,
    isColumnHidden,
    onEditLog,
    onAddContact,
    onMarkVoicemail,
    onReportCall,
}) => {
    const [isVoicemailPlaybackOpen, setIsVoicemailPlaybackOpen] =
        useState(false);
    const reportCallModal = useModal();

    const call_type =
        entry?.call_recording &&
        entry?.system_result === PhoneUtil.SYSTEM_RESULT_OPTIONS[1].value
            ? "voicemail"
            : entry?.call_recording
            ? "call_recording"
            : "";

    return (
        <Table.Row>
            <Table.Cell
                className={
                    entry?.system_result ===
                        PhoneUtil.SYSTEM_RESULT_OPTIONS[1].value &&
                    entry?.unreturned
                        ? "CallHistoryTable__RedCellIndicator"
                        : ""
                }
                title={
                    entry?.system_result ===
                        PhoneUtil.SYSTEM_RESULT_OPTIONS[1].value &&
                    entry?.unreturned
                        ? "Call Not Returned"
                        : ""
                }
            />
            {!isColumnHidden(KEY_DATE) && (
                <Table.Cell
                    className="CallHistoryTable__MediumFixedWidth"
                    width={2}
                >
                    {entry?.start_ts && (
                        <>
                            <strong>
                                {DateTimeUtils.formatEpoch(
                                    entry.start_ts,
                                    DateTimeUtils.WORD_DATE_FORMAT
                                )}
                            </strong>
                            <br />
                            {DateTimeUtils.formatEpoch(
                                entry.start_ts,
                                DateTimeUtils.TIME_FORMAT
                            )}
                        </>
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CONTACT_ACCOUNT) && (
                <Table.Cell
                    className="CallHistoryTable__MediumFixedWidth"
                    width={2}
                >
                    {entry?.record?.name ? (
                        <>
                            <Icon
                                className={`grey fa-solid fa-${
                                    entry?.record?.contact_type === "household"
                                        ? "house"
                                        : entry?.record?.contact_type ===
                                          "business"
                                        ? "building"
                                        : "user"
                                }`}
                            />

                            {entry?.record?.account_id ||
                            entry?.record?.contact_id ? (
                                <Link
                                    to={
                                        entry?.record?.contact_id
                                            ? `/contact/${entry?.record?.contact_id}`
                                            : entry?.record?.account_id
                                            ? `/account/${entry?.record?.account_id}`
                                            : ""
                                    }
                                >
                                    {entry?.record?.name}
                                </Link>
                            ) : (
                                entry?.record?.name
                            )}
                        </>
                    ) : (
                        "Unknown"
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_PHONE_NUMBER) && (
                <Table.Cell
                    className="CallHistoryTable__MediumFixedWidth"
                    width={2}
                >
                    {PhoneUtil.formatPhoneNumber(entry?.phone)}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_USER) && (
                <Table.Cell width={1}>
                    <div style={{ display: "flex" }}>
                        <AvatarGroup
                            persons={entry?.users?.map((user) => ({
                                ...user,
                                inactive: !user.active,
                            }))}
                            limit={3}
                            size="3em"
                        />
                    </div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_DIRECTION) && (
                <Table.Cell className="CallHistoryTable__Capitalize" width={2}>
                    {entry?.direction}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_SYSTEM_RESULT) && (
                <Table.Cell
                    className={`CallHistoryTable__Capitalize ${
                        entry?.system_result ===
                        PhoneUtil.SYSTEM_RESULT_OPTIONS[1].value
                            ? "CallHistoryTable__FailureText"
                            : "CallHistoryTable__SucessText"
                    }`}
                    width={2}
                >
                    {entry?.system_result}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_USER_RESULT) && (
                <Table.Cell className="CallHistoryTable__SucessText" width={2}>
                    {entry?.user_result}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_DURATION) && (
                <Table.Cell className="CallHistoryTable__SmallFixedWidth CallHistoryTable__ActionColumn">
                    {call_type && (
                        <Popup
                            style={{ minWidth: 300 }}
                            trigger={
                                <Icon
                                    className={`blue fa-solid fa-${
                                        call_type === "call_recording"
                                            ? "circle-play"
                                            : "voicemail"
                                    }`}
                                    onClick={() =>
                                        setIsVoicemailPlaybackOpen(true)
                                    }
                                    disabled={!entry?.call_recording}
                                    title={
                                        call_type === "call_recording"
                                            ? "Play Call Recording"
                                            : "Play Voicemail"
                                    }
                                />
                            }
                            on="click"
                            content={
                                <VoicemailPlayback
                                    onClose={() =>
                                        setIsVoicemailPlaybackOpen(false)
                                    }
                                    className={`audio-playback-${entry?.call_uuid}`}
                                    fileName={
                                        call_type === "call_recording"
                                            ? "Call Recording"
                                            : "Voicemail"
                                    }
                                    link={entry?.call_recording}
                                    isVoicemail={call_type === "voicemail"}
                                    onMarkAsListened={() => {
                                        if (entry?.listened) return;
                                        onMarkVoicemail(
                                            entry?.call_uuid,
                                            true,
                                            false
                                        );
                                    }}
                                />
                            }
                            position="bottom center"
                            open={isVoicemailPlaybackOpen}
                            onOpen={() => setIsVoicemailPlaybackOpen(true)}
                        />
                    )}
                    <span className="CallHistoryTable__TimeContainer">
                        {entry?.length
                            ? PhoneUtil.timeTicker(entry?.length)
                            : "00:00:00"}
                    </span>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_DESTINATION) && (
                <Table.Cell
                    className="CallHistoryTable__SmallFixedWidth"
                    width={2}
                >
                    {PhoneUtil.DESTINATION_TYPES_ICONS[
                        entry?.destination?.type
                    ] && (
                        <Icon
                            className={
                                PhoneUtil.DESTINATION_TYPES_ICONS[
                                    entry?.destination?.type
                                ]
                            }
                        />
                    )}{" "}
                    {PhoneUtil.DESTINATION_TYPES_URLS[
                        entry?.destination?.type
                    ] ? (
                        <Link
                            to={`${
                                PhoneUtil.DESTINATION_TYPES_URLS[
                                    entry?.destination?.type
                                ]
                            }?search=${
                                entry?.destination?.type === "phone_number"
                                    ? entry?.destination?.alias
                                    : entry?.destination?.label
                            }${
                                entry?.destination?.type === "call_queue"
                                    ? "&type=call_queue"
                                    : entry?.destination?.type === "ring_group"
                                    ? "&type=ring_group"
                                    : entry?.destination?.type ===
                                      "company_voicemail"
                                    ? "&type=company_voicemail"
                                    : entry?.destination?.type ===
                                      "conference_room"
                                    ? "&type=conference_room"
                                    : entry?.destination?.type === "parking_lot"
                                    ? "&type=parking_lot"
                                    : ""
                            }`}
                        >
                            {entry?.destination?.label}
                        </Link>
                    ) : (
                        entry?.destination?.label
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CALL_TAGS) && (
                <Table.Cell
                    className="CallHistoryTable__MediumFixedWidth"
                    width={2}
                >
                    {entry?.tags?.map((t) => t?.name)?.join("/ ")}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_NOTE) && (
                <Table.Cell
                    className="CallHistoryTable__MediumFixedWidth"
                    width={2}
                >
                    {entry?.note && <ViewMore text={entry.note} />}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_ACTIONS) && (
                <Table.Cell className="CallHistoryTable__SmallFixedWidth CallHistoryTable__ActionColumn">
                    <Popup
                        trigger={
                            <Icon
                                onClick={() => onEditLog(entry)}
                                className="fa-solid fa-pencil blue"
                                title="Edit"
                            />
                        }
                        content="Edit"
                        position="top center"
                        inverted
                    />
                    <Popup
                        style={{ minWidth: 310 }}
                        trigger={
                            <Icon
                                className="fa-solid fa-warning blue"
                                title="Report Call Problem"
                            />
                        }
                        on="click"
                        content={
                            <ReportCallPopup
                                entry={entry}
                                onClose={reportCallModal?.closeModal}
                                onReportCall={onReportCall}
                            />
                        }
                        position="bottom right"
                        open={reportCallModal?.isVisible}
                        onOpen={reportCallModal?.openModal}
                        onClose={reportCallModal?.closeModal}
                    />
                    {call_type === "voicemail" && (
                        <Popup
                            trigger={
                                <Icon
                                    className={`fa-solid blue ${
                                        entry?.listened
                                            ? "fa-circle"
                                            : "fa-circle-dashed"
                                    }`}
                                    onClick={() =>
                                        onMarkVoicemail(
                                            entry?.call_uuid,
                                            !!!entry?.listened
                                        )
                                    }
                                />
                            }
                            content={
                                entry?.listened
                                    ? "Mark as Unlistened"
                                    : "Mark as Listened"
                            }
                            position="top center"
                            inverted
                        />
                    )}
                    {!entry?.record?.account_id &&
                        !entry?.record?.contact_id && (
                            <Icon
                                onClick={() => onAddContact(entry)}
                                className="fa-solid fa-circle-plus blue"
                                title="Add Contact"
                            />
                        )}
                </Table.Cell>
            )}
        </Table.Row>
    );
};

CallHistoryRow.propTypes = {
    entry: PropTypes.object.isRequired,
    isColumnHidden: PropTypes.func.isRequired,
    onEditLog: PropTypes.func,
    onAddContact: PropTypes.func,
    onMarkVoicemail: PropTypes.func,
    onReportCall: PropTypes.func,
};

export default CallHistoryRow;
